import type { FC } from 'react';

import { useCms, useContent } from '@eneco/cms';
import { Box, IconButton, Stack, Text } from '@eneco/sparky';
import { FacebookIcon, InstagramIcon, LinkedInIcon, XIcon, YouTubeIcon } from '@eneco/sparky/icons';

import type { SocialMediaMenuContent } from './social-media-menu-content.types';

/**
 * `SocialMediaMenu` block for the CMS-agnostic lane, serving **both** providers. Mirrors the legacy
 * component (`libs/sitecore/components/content/src/Footer/SocialMediaMenu.tsx`) — an optional bold title
 * above a row of round icon buttons, one per resolved link.
 *
 * Its icon-name-to-brand lookup is ported inline rather than imported: the legacy `SocialMediaIcon`
 * lives in `lib:sitecore-components`, which this lane's module boundary (`nx.json`) does not permit
 * depending on.
 *
 * See `social-media-menu-content.types.ts` for the (not yet live-confirmed) content shape and the
 * Sitecore-lane fallback (`items`) — that lane's adapter never nests content under `siteWideContent`.
 */
const SocialMediaMenu: FC = () => {
  const { adapter } = useCms();
  const content = useContent<SocialMediaMenuContent>();

  const title = content.title ?? content.siteWideContent?.title;
  const items = content.siteWideContent?.items
    ? content.siteWideContent.items.flatMap((item, index) => {
        const link = adapter.toLink(item.link);
        return link ? [{ key: item._key ?? String(index), link, icon: item.icon ?? '' }] : [];
      })
    : (content.items ?? []).flatMap((item, index) => {
        const link = adapter.toLink(item.link);
        return link ? [{ key: item.id ?? String(index), link, icon: item.iconName ?? '' }] : [];
      });

  if (items.length === 0) return null;

  return (
    <Stack alignX={{ initial: 'start', md: 'end' }} gap="2">
      {title && (
        <Stack.Item>
          <Text color="textPrimary" weight="bold">
            {title}
          </Text>
        </Stack.Item>
      )}
      <Stack.Item>
        <Stack alignX="end" as="ul" direction="row" gap="2" wrap="true">
          {items.map(({ key, link, icon }) => (
            <Stack.Item as="li" key={key}>
              <Box backgroundColor="backgroundPrimary" borderRadius="round">
                <IconButton href={link.href} label={link.text || 'Social media'} size="large">
                  <SocialMediaIcon iconName={icon} />
                </IconButton>
              </Box>
            </Stack.Item>
          ))}
        </Stack>
      </Stack.Item>
    </Stack>
  );
};

/**
 * The legacy lookup (`SocialMediaIcon.tsx`) picked a brand via an initial-value-less `reduce` that tests
 * `iconName.includes(accumulator)` rather than `includes(currentBrand)` — it happens to land on the
 * right brand when one matches, but silently defaults to `'youtube'` (the array's last element) rather
 * than `'facebook'` for an unrecognised name, since the switch's `facebook`/`default` case is then never
 * reached. Replaced with an explicit `find` + fallback so an unmatched icon name visibly does what the
 * component always intended: fall back to the Facebook icon.
 */
const SocialMediaIcon: FC<{ iconName: string }> = ({ iconName }) => {
  const arrSocialBrands = ['facebook', 'instagram', 'linkedin', 'twitter', 'youtube'];
  const matchedBrand = arrSocialBrands.find(brand => iconName.toLowerCase().includes(brand)) ?? 'facebook';

  switch (matchedBrand) {
    case 'youtube':
      return <YouTubeIcon />;
    case 'linkedin':
      return <LinkedInIcon />;
    case 'twitter':
      return <XIcon />;
    case 'instagram':
      return <InstagramIcon />;
    case 'facebook':
    default:
      return <FacebookIcon />;
  }
};

export default SocialMediaMenu;
